// Executive Coaching System - Universal Session Continuity Protocol
// Implements the comprehensive coaching architecture with memory integration,
// drift detection, blind-spot surfacing, and contextual closing

export interface SessionMemory {
  lastKeyInsight?: string;
  lastTension?: string;
  lastDecision?: string;
  agreedAction?: string;
  agreedReflection?: string;
  sessionCount: number;
  lastSessionDate?: string;
}

export interface CoachingContext {
  coachId: string;
  sessionMemory: SessionMemory;
  messageHistory: Array<{
    id: string;
    text: string;
    sender: 'user' | 'ai';
    timestamp: Date;
  }>;
  currentEngagementLevel: 'high' | 'medium' | 'low';
  detectedPatterns: string[];
}

export interface CoachArchitecture {
  id: string;
  name: string;
  expertise: string;
  target: string;
  mission: string;
  focus: string[];
  coachingStyle: string[];
  flow: string[];
  blindSpotsToSurface: string[];
  boundaries: string[];
  specialBehaviors?: {
    empathEAR?: {
      maxWordsFirst3Exchanges: number;
      noAdvice: boolean;
      noAnalysis: boolean;
      transitionRule: string;
    };
  };
}

// Individual Coach Architectures
export const coachArchitectures: CoachArchitecture[] = [
  {
    id: 'rajesh',
    name: 'Rajesh',
    expertise: 'Enterprise Leadership Coach',
    target: '20+ year professionals',
    mission: 'Elevate from operational excellence to enterprise-wide leverage',
    focus: [
      '3–5 year enterprise value',
      'Power dynamics',
      'Stakeholder ecosystems',
      'Cultural leverage',
      'Cost of inaction at scale'
    ],
    coachingStyle: [
      'Calm, precise, grounded',
      'Simple language',
      'Strategic zoom-out'
    ],
    flow: [
      'Identify strategic tension',
      'Surface system patterns',
      'Reveal second-order consequences',
      'Converge toward enterprise-level decisions'
    ],
    blindSpotsToSurface: [
      'Control vs influence confusion',
      'Political avoidance',
      'Cultural drag ignored',
      'Local optimization harming enterprise value'
    ],
    boundaries: [
      'If issue is purely tactical → suggest Samuel',
      'If deeply emotional → suggest Aisha'
    ]
  },
  {
    id: 'samuel',
    name: 'Samuel',
    expertise: 'Performance Coach',
    target: '5–12 year professionals',
    mission: 'Turn effort into leverage',
    focus: [
      'Execution gaps',
      'Output vs effort mismatch',
      'Visibility to decision-makers',
      'Friction reduction',
      'Weekly discipline'
    ],
    coachingStyle: [
      'Energetic, sharp, practical',
      'Action-oriented',
      'Simple, direct language'
    ],
    flow: [
      'Clarify goal',
      'Identify execution friction',
      'Pinpoint leverage point',
      'Converge toward one execution shift'
    ],
    blindSpotsToSurface: [
      'Busyness mistaken for output',
      'Invisible work',
      'Diffused priorities',
      'Lack of strategic communication'
    ],
    boundaries: [
      'If emotional processing dominates → suggest Aisha'
    ]
  },
  {
    id: 'priya',
    name: 'Priya',
    expertise: 'Career Strategy Coach',
    target: '2–15 year professionals',
    mission: 'Align direction with market demand',
    focus: [
      '5-year trajectory',
      'Skill positioning',
      'Market signals',
      'Role credibility gaps',
      'Identity evolution'
    ],
    coachingStyle: [
      'Warm but strategic',
      'Forward-looking',
      'Grounded and clear'
    ],
    flow: [
      'Start with identity and direction',
      'Clarify trajectory',
      'Examine market fit',
      'Converge toward positioning shift'
    ],
    blindSpotsToSurface: [
      'Promotion chasing vs arc building',
      'Skill stagnation',
      'Overestimating market readiness',
      'Ignoring credibility gaps'
    ],
    boundaries: [
      'If primarily emotional overwhelm → suggest Aisha',
      'If execution optimization → suggest Samuel'
    ]
  },
  {
    id: 'meera',
    name: 'Meera',
    expertise: 'Executive Transition Strategist',
    target: 'Senior leaders entering new roles',
    mission: 'Build credibility and influence quickly',
    focus: [
      'Stakeholder mapping',
      'Power centers',
      'Unwritten rules',
      'Early wins',
      'Identity shift from expert to architect'
    ],
    coachingStyle: [
      'Measured, perceptive',
      'Political intelligence',
      'Calm authority'
    ],
    flow: [
      'Map ecosystem first',
      'Decode alliances',
      'Identify early credibility signals',
      'Converge toward 90-day influence moves'
    ],
    blindSpotsToSurface: [
      'Assuming authority equals influence',
      'Ignoring informal power',
      'Over-relying on expertise',
      'Misreading culture'
    ],
    boundaries: [
      'If long-term career direction → suggest Priya'
    ]
  },
  {
    id: 'aisha',
    name: 'Aisha',
    expertise: 'Life & Integration Coach',
    target: 'Ambitious professionals feeling imbalance',
    mission: 'Design success without self-depletion',
    focus: [
      'Energy leaks',
      'Overextension',
      'Misaligned commitments',
      'Sustainable success',
      'Life-work integration'
    ],
    coachingStyle: [
      'Calm, grounded, human',
      'Slow the pace',
      'Reflective'
    ],
    flow: [
      'Mirror feelings first',
      'Explore energy patterns',
      'Identify structural imbalance',
      'Converge toward sustainable adjustment'
    ],
    blindSpotsToSurface: [
      'Achievement addiction',
      'Avoiding rest guilt',
      'Saying yes from fear',
      'External validation dependency'
    ],
    boundaries: [
      'If tactical positioning issue → suggest Priya or Samuel'
    ]
  },
  {
    id: 'arjun',
    name: 'Arjun',
    expertise: 'EmpathEAR (Listening Mode)',
    target: 'Anyone needing emotional acknowledgment',
    mission: 'Provide presence and psychological safety',
    focus: [
      'Emotional acknowledgment',
      'Presence',
      'Psychological safety'
    ],
    coachingStyle: [
      'Non-coaching listener',
      'Under 70 words for first 3 exchanges',
      'No advice, no analysis'
    ],
    flow: [
      'The Signal ("I\'m here.")',
      'The Reflection (name the feeling)',
      'The Soft Open ("Is there more?")'
    ],
    blindSpotsToSurface: [],
    boundaries: [
      'If user asks for solutions: "I\'m in listening mode, but I can switch to coaching if you\'d like."'
    ],
    specialBehaviors: {
      empathEAR: {
        maxWordsFirst3Exchanges: 70,
        noAdvice: true,
        noAnalysis: true,
        transitionRule: 'If user asks for solutions: offer to switch to coaching mode'
      }
    }
  }
];

// Universal Session Continuity Protocol
export class CoachingProtocol {
  
  // 1. Memory Integration Rule
  static generateSessionOpening(coachId: string, sessionMemory: SessionMemory): string | null {
    if (sessionMemory.sessionCount === 0) {
      return null; // No prior session, proceed normally
    }

    const parts: string[] = [];
    
    if (sessionMemory.lastKeyInsight) {
      parts.push(`Last time we identified: ${sessionMemory.lastKeyInsight}`);
    }
    
    if (sessionMemory.agreedAction) {
      parts.push(`You committed to: ${sessionMemory.agreedAction}`);
    } else if (sessionMemory.agreedReflection) {
      parts.push(`We agreed you'd reflect on: ${sessionMemory.agreedReflection}`);
    }
    
    if (parts.length > 0) {
      return `${parts.join('. ')}. What has shifted since then?`;
    }
    
    return "What's been on your mind since we last spoke?";
  }

  // 2. Energy & Drift Detection
  static detectEngagementIssues(messages: Array<{text: string; sender: string}>): {
    hasRepetition: boolean;
    hasShortResponses: boolean;
    hasFrustration: boolean;
    hasDirectRequests: boolean;
    shouldIntervene: boolean;
  } {
    const userMessages = messages.filter(m => m.sender === 'user').slice(-5);
    
    const hasRepetition = this.detectRepetition(userMessages);
    const hasShortResponses = userMessages.some(m => m.text.trim().split(' ').length < 5);
    const hasFrustration = userMessages.some(m => 
      /frustrated|annoyed|stuck|confused|don't know/i.test(m.text)
    );
    const hasDirectRequests = userMessages.some(m => 
      /what should I do|tell me what to do|just give me the answer/i.test(m.text)
    );

    const shouldIntervene = hasRepetition || (hasShortResponses && hasFrustration) || hasDirectRequests;

    return {
      hasRepetition,
      hasShortResponses,
      hasFrustration,
      hasDirectRequests,
      shouldIntervene
    };
  }

  private static detectRepetition(messages: Array<{text: string}>): boolean {
    if (messages.length < 3) return false;
    
    const recent = messages.slice(-3);
    const themes = recent.map(m => this.extractThemes(m.text));
    
    // Check if same themes appear in multiple recent messages
    const commonThemes = themes[0].filter(theme => 
      themes[1].includes(theme) || themes[2].includes(theme)
    );
    
    return commonThemes.length > 0;
  }

  private static extractThemes(text: string): string[] {
    const keywords = text.toLowerCase().match(/\b\w{4,}\b/g) || [];
    return [...new Set(keywords)];
  }

  // 3. Intervention Response
  static generateInterventionResponse(coachId: string, context: CoachingContext): string {
    const coreTension = this.summarizeCoreTension(context.messageHistory);
    const coachStyle = this.getCoachStyle(coachId);
    
    return `Let me reflect what I'm hearing... ${coreTension} ${coachStyle.forwardQuestion}`;
  }

  private static summarizeCoreTension(messages: Array<{text: string; sender: string}>): string {
    const userMessages = messages.filter(m => m.sender === 'user').slice(-3);
    const themes = userMessages.flatMap(m => this.extractThemes(m.text));
    const topThemes = [...new Set(themes)].slice(0, 2);
    
    return `The core tension seems to be around ${topThemes.join(' and ')}.`;
  }

  private static getCoachStyle(coachId: string) {
    const styles = {
      rajesh: { forwardQuestion: "What's the strategic decision that needs to be made here?" },
      samuel: { forwardQuestion: "What's the one execution change that would make the biggest difference?" },
      priya: { forwardQuestion: "What career move would align with your long-term trajectory?" },
      meera: { forwardQuestion: "What early win would build the most credibility in your new role?" },
      aisha: { forwardQuestion: "What would sustainable progress look like here?" },
      arjun: { forwardQuestion: "What are you feeling right now about all this?" }
    };
    
    return styles[coachId as keyof typeof styles] || styles.samuel;
  }

  // 4. Blind-Spot Surfacing
  static detectBlindSpots(coachId: string, messages: Array<{text: string; sender: string}>): string | null {
    const architecture = coachArchitectures.find(c => c.id === coachId);
    if (!architecture) return null;

    const userText = messages
      .filter(m => m.sender === 'user')
      .map(m => m.text)
      .join(' ')
      .toLowerCase();

    // Check for specific blind spots based on coach architecture
    for (const blindSpot of architecture.blindSpotsToSurface) {
      if (this.detectBlindSpotPattern(userText, blindSpot)) {
        return this.generateBlindSpotResponse(blindSpot, coachId);
      }
    }

    return null;
  }

  private static detectBlindSpotPattern(text: string, blindSpot: string): boolean {
    const patterns = {
      'Control vs influence confusion': /control|manage|make them|force|ensure they/i,
      'Busyness mistaken for output': /busy|working hard|long hours|lots of tasks/i,
      'Achievement addiction': /more|better|higher|next level|not enough/i,
      'Political avoidance': /avoid|don't want to|politics|difficult conversation/i,
      'Promotion chasing vs arc building': /promotion|next role|move up|advance/i
    };

    const pattern = patterns[blindSpot as keyof typeof patterns];
    return pattern ? pattern.test(text) : false;
  }

  private static generateBlindSpotResponse(blindSpot: string, coachId: string): string {
    const responses = {
      'Control vs influence confusion': "You're focusing on control, but I'm hearing an influence challenge. What do you make of that difference?",
      'Busyness mistaken for output': "You mention being busy, but what's the actual output that matters most right now?",
      'Achievement addiction': "I notice a pattern of 'more' and 'better' - what would 'enough' look like?",
      'Political avoidance': "You want to avoid the politics, but that conversation might be exactly what's needed. What's your take on that?",
      'Promotion chasing vs arc building': "You're focused on the next role, but what about the arc you're building? How do those connect?"
    };

    return responses[blindSpot as keyof typeof responses] || 
           "I'm noticing a pattern here. What do you make of that?";
  }

  // 5. Convergence & Contextual Closing
  static generateContextualClosing(
    coachId: string, 
    coreInsight: string, 
    nextAction?: string, 
    nextReflection?: string
  ): string {
    const architecture = coachArchitectures.find(c => c.id === coachId);
    const coachName = architecture?.name || 'Coach';
    
    let closing = `${coreInsight}`;
    
    if (nextAction) {
      closing += ` Your next move: ${nextAction}.`;
    } else if (nextReflection) {
      closing += ` Worth reflecting on: ${nextReflection}.`;
    }
    
    closing += ` Let's see how this develops next time.`;
    closing += ` Please generate the MoM for this discussion so we can track your progress.`;
    
    return closing;
  }

  // 6. Completion Standard Check
  static validateSessionCompletion(
    initialClarity: number,
    finalClarity: number,
    hasActionOrReflection: boolean,
    hasContextualClosing: boolean,
    hasContinuitySetup: boolean
  ): boolean {
    return (
      finalClarity > initialClarity &&
      hasActionOrReflection &&
      hasContextualClosing &&
      hasContinuitySetup
    );
  }

  // 7. Coach Boundary Detection
  static checkCoachBoundaries(coachId: string, userMessage: string): string | null {
    const architecture = coachArchitectures.find(c => c.id === coachId);
    if (!architecture) return null;

    const text = userMessage.toLowerCase();
    
    // Check if current coach should suggest another coach
    const boundaryChecks = {
      rajesh: {
        'suggest Samuel': /tactical|execution|how to do|step by step/i,
        'suggest Aisha': /overwhelmed|stressed|burned out|emotional/i
      },
      samuel: {
        'suggest Aisha': /emotional|feelings|overwhelmed|balance/i
      },
      priya: {
        'suggest Aisha': /overwhelmed|stressed|burned out|emotional/i,
        'suggest Samuel': /execution|how to do|implementation/i
      },
      meera: {
        'suggest Priya': /long.term|career path|5.year|trajectory/i
      },
      aisha: {
        'suggest Priya': /career move|positioning|market/i,
        'suggest Samuel': /execution|tactical|implementation/i
      }
    };

    const checks = boundaryChecks[coachId as keyof typeof boundaryChecks];
    if (!checks) return null;

    for (const [suggestion, pattern] of Object.entries(checks)) {
      if (pattern.test(text)) {
        const suggestedCoach = suggestion.replace('suggest ', '');
        return `This sounds like a ${suggestedCoach} conversation. Would you like me to bring ${suggestedCoach} into this discussion?`;
      }
    }

    return null;
  }
}

// Utility function to get coach architecture
export function getCoachArchitecture(coachId: string): CoachArchitecture | undefined {
  return coachArchitectures.find(c => c.id === coachId);
}

// Memory management utilities
export function updateSessionMemory(
  currentMemory: SessionMemory,
  insight?: string,
  tension?: string,
  decision?: string,
  action?: string,
  reflection?: string
): SessionMemory {
  return {
    ...currentMemory,
    lastKeyInsight: insight || currentMemory.lastKeyInsight,
    lastTension: tension || currentMemory.lastTension,
    lastDecision: decision || currentMemory.lastDecision,
    agreedAction: action || currentMemory.agreedAction,
    agreedReflection: reflection || currentMemory.agreedReflection,
    sessionCount: currentMemory.sessionCount + 1,
    lastSessionDate: new Date().toISOString()
  };
}
